import { useCallback, useEffect, useState } from 'react';
import interactionsService, {
  type Interaction,
  type InteractionKind,
  type InteractionOutcome,
} from '../../services/interactions.service';
import { tokens } from '../forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { humanize } from '../../lib/stringCase';
import { colors } from '../../styles/theme';
import ScheduleCallbackButton from './ScheduleCallbackButton';
import { readerLocale } from '../../i18n/language';

interface Props {
  /** The case_id this interaction is primarily about. */
  primaryCaseId: string;
}

const KIND_LABELS: Record<InteractionKind, string> = {
  phone_outbound: 'Phone — outbound (we called)',
  phone_inbound: 'Phone — inbound (client called)',
  email_outbound: 'Email — outbound',
  email_inbound: 'Email — inbound',
};

const OUTCOME_LABELS: Record<InteractionOutcome, string> = {
  connected: 'Connected (spoke with client)',
  voicemail_left: 'Voicemail left',
  no_answer: 'No answer',
  busy: 'Busy',
  wrong_number: 'Wrong number',
  client_hung_up: 'Client hung up',
  callback_scheduled: 'Callback scheduled',
};

/**
 * Staff-facing recorder for one staff↔client interaction tied to a
 * specific case. Two-phase flow:
 *
 *   1. "Start interaction" creates the row with `outcome='no_answer'`
 *      as a placeholder and stamps `started_at` immediately. This
 *      lets the audit log capture every attempt — connected or not.
 *   2. The agent fills in outcome + notes + (optionally) commitment
 *      made / next action / ended_at and clicks "Wrap up" to finalize.
 *
 * Renders the most recent interaction inline so the agent sees the
 * row they just started. List of all interactions on this case lives
 * in a separate `InteractionsList` (not bundled here to keep the
 * recorder focused on one operation at a time).
 */
export default function InteractionRecorder({ primaryCaseId }: Readonly<Props>): JSX.Element {
  const [current, setCurrent] = useState<Interaction | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Start-form state
  const [kind, setKind] = useState<InteractionKind>('phone_outbound');

  // Wrap-form state (only enabled once an interaction is in flight)
  const [outcome, setOutcome] = useState<InteractionOutcome>('connected');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [commitment, setCommitment] = useState('');

  const refreshLatest = useCallback(async () => {
    try {
      const rows = await interactionsService.listForCase(primaryCaseId);
      setCurrent(rows[0] ?? null);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load interactions.'));
    }
  }, [primaryCaseId]);

  useEffect(() => {
    refreshLatest();
  }, [refreshLatest]);

  const handleStart = async (): Promise<void> => {
    setError(null);
    setFlash(null);
    setBusy(true);
    try {
      await interactionsService.start(primaryCaseId, { kind });
      await refreshLatest();
      setFlash('Interaction started. Wrap it up below when the call ends.');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not start the interaction.'));
    } finally {
      setBusy(false);
    }
  };

  const handleWrap = async (): Promise<void> => {
    if (!current) return;
    if (outcome === 'connected' && !notes.trim()) {
      setError('Add a note describing the call before saving.');
      return;
    }
    setError(null);
    setFlash(null);
    setBusy(true);
    try {
      await interactionsService.update(current.id, {
        outcome,
        ended_at: new Date().toISOString(),
        notes: notes.trim() || undefined,
        next_action_required: nextAction.trim() || undefined,
        commitment_made: commitment.trim() || undefined,
      });
      setFlash('Interaction recorded.');
      setNotes('');
      setNextAction('');
      setCommitment('');
      await refreshLatest();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not wrap up the interaction.'));
    } finally {
      setBusy(false);
    }
  };

  // Discard an abandoned placeholder (started by mistake). Soft-deleted +
  // audited server-side; a finalized interaction can't be discarded. Finding #3.
  const handleDiscard = async (): Promise<void> => {
    if (!current) return;
    setError(null);
    setFlash(null);
    setBusy(true);
    try {
      await interactionsService.discard(current.id);
      setCurrent(null);
      setNotes('');
      setNextAction('');
      setCommitment('');
      setFlash('Interaction discarded — nothing was saved.');
      await refreshLatest();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not discard the interaction.'));
    } finally {
      setBusy(false);
    }
  };

  const isOpen =
    current !== null &&
    current.ended_at === null &&
    current.outcome === 'no_answer';

  // Don't save an empty wrap-up: a "connected" call must carry a note; other
  // outcomes (no answer, voicemail, busy…) are self-describing. Finding #3.
  const canWrap = !busy && !(outcome === 'connected' && notes.trim().length === 0);

  return (
    <div style={cardStyle}>
      <h3 style={h3Style}>Record interaction</h3>

      {flash ? <Notice kind="success" message={flash} /> : null}
      {error ? <Notice kind="danger" message={error} /> : null}

      {isOpen ? null : (
        <StartForm kind={kind} busy={busy} onKindChange={setKind} onStart={handleStart} />
      )}

      {isOpen && current ? (
        <WrapForm
          current={current}
          outcome={outcome}
          notes={notes}
          nextAction={nextAction}
          commitment={commitment}
          busy={busy}
          canWrap={canWrap}
          onOutcomeChange={setOutcome}
          onNotesChange={setNotes}
          onNextActionChange={setNextAction}
          onCommitmentChange={setCommitment}
          onWrap={handleWrap}
          onDiscard={handleDiscard}
          onScheduled={refreshLatest}
        />
      ) : null}

      {current && !isOpen ? <RecentInteraction current={current} /> : null}
    </div>
  );
}

function Notice({
  kind,
  message,
}: Readonly<{ kind: 'success' | 'danger'; message: string }>): JSX.Element {
  const isSuccess = kind === 'success';
  return (
    <div
      style={{
        background: isSuccess ? colors.successBg : colors.dangerBg,
        color: isSuccess ? colors.success : colors.danger,
        border: `1px solid ${isSuccess ? colors.successBorder : colors.dangerBorder}`,
        borderRadius: 8,
        padding: 8,
        fontSize: 12,
        marginBottom: 10,
      }}
    >
      {message}
    </div>
  );
}

function StartForm({
  kind,
  busy,
  onKindChange,
  onStart,
}: Readonly<{
  kind: InteractionKind;
  busy: boolean;
  onKindChange: (k: InteractionKind) => void;
  onStart: () => void;
}>): JSX.Element {
  return (
    <div style={sectionStyle}>
      <label style={labelStyle}>
        Channel
        {/* */}
        <select
          value={kind}
          onChange={(e) => onKindChange(e.target.value as InteractionKind)}
          style={inputStyle}
        >
          {Object.entries(KIND_LABELS)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
        </select>
      </label>
      <button type="button" onClick={onStart} disabled={busy} style={btnPrimary}>
        {busy ? 'Starting…' : 'Start interaction'}
      </button>
    </div>
  );
}

function WrapForm({
  current,
  outcome,
  notes,
  nextAction,
  commitment,
  busy,
  canWrap,
  onOutcomeChange,
  onNotesChange,
  onNextActionChange,
  onCommitmentChange,
  onWrap,
  onDiscard,
  onScheduled,
}: Readonly<{
  current: Interaction;
  outcome: InteractionOutcome;
  notes: string;
  nextAction: string;
  commitment: string;
  busy: boolean;
  canWrap: boolean;
  onOutcomeChange: (o: InteractionOutcome) => void;
  onNotesChange: (v: string) => void;
  onNextActionChange: (v: string) => void;
  onCommitmentChange: (v: string) => void;
  onWrap: () => void;
  onDiscard: () => void;
  onScheduled: () => void;
}>): JSX.Element {
  return (
    <div style={sectionStyle}>
      <p style={{ fontSize: 12, color: tokens.textMuted, margin: '0 0 10px' }}>
        In-flight interaction · started{' '}
        {new Date(current.started_at).toLocaleTimeString(readerLocale())} · channel{' '}
        <strong>{humanize(current.kind)}</strong>
      </p>

      <label style={labelStyle}>
        Outcome
        {/* */}
        <select
          value={outcome}
          onChange={(e) => onOutcomeChange(e.target.value as InteractionOutcome)}
          style={inputStyle}
        >
          {Object.entries(OUTCOME_LABELS)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
        </select>
      </label>

      <label style={labelStyle}>
        Notes (free-form)
        {/* */}
        <textarea
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          maxLength={10_000}
          rows={4}
          style={inputStyle}
          placeholder="Conversation summary, agreements, concerns the client mentioned…"
        />
      </label>

      <label style={labelStyle}>
        Next action required
        {/* */}
        <input
          type="text"
          value={nextAction}
          onChange={(e) => onNextActionChange(e.target.value)}
          maxLength={500}
          style={inputStyle}
          placeholder="callback Tuesday 10am, send statement, escalate to compliance…"
        />
      </label>

      <label style={labelStyle}>
        Commitment made (by the client)
        {/* */}
        <input
          type="text"
          value={commitment}
          onChange={(e) => onCommitmentChange(e.target.value)}
          maxLength={500}
          style={inputStyle}
          placeholder="client agreed to pay $5k by Friday…"
        />
      </label>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onWrap}
          disabled={!canWrap}
          title={canWrap ? undefined : 'Add a note describing a connected call before saving'}
          style={{ ...btnPrimary, ...(canWrap ? {} : { opacity: 0.6, cursor: 'not-allowed' }) }}
        >
          {busy ? 'Wrapping up…' : 'Wrap up & save'}
        </button>
        <ScheduleCallbackButton
          interactionId={current.id}
          initialPurpose={nextAction}
          disabled={busy}
          onScheduled={onScheduled}
        />
        <button
          type="button"
          onClick={onDiscard}
          disabled={busy}
          title="Discard this in-flight interaction without saving"
          style={{
            ...btnPrimary,
            background: 'transparent',
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}

function RecentInteraction({ current }: Readonly<{ current: Interaction }>): JSX.Element {
  return (
    <div style={sectionStyle}>
      <h4 style={h4Style}>Most recent</h4>
      <p style={{ fontSize: 13, margin: 0 }}>
        <strong>{humanize(current.kind)}</strong> ·{' '}
        <em>{humanize(current.outcome)}</em>{' '}
        <span style={{ color: tokens.textMuted, fontSize: 12 }}>
          · {new Date(current.started_at).toLocaleString(readerLocale())}
          {current.duration_seconds === null
            ? ''
            : ` · ${Math.round(current.duration_seconds / 60)} min`}
        </span>
      </p>
      {current.notes ? (
        <p style={{ fontSize: 12, color: tokens.textMuted, margin: '6px 0 0' }}>
          {current.notes}
        </p>
      ) : null}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
  color: tokens.textPrimary,
};
const h3Style: React.CSSProperties = { margin: '0 0 12px 0', fontSize: 14 };
const h4Style: React.CSSProperties = {
  margin: '0 0 6px 0',
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: tokens.gold,
};
const sectionStyle: React.CSSProperties = {
  paddingBottom: 12,
  marginBottom: 12,
  borderBottom: `1px solid ${tokens.border}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
  alignSelf: 'flex-start',
};
